import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import {
  
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Bookmark,
  Heart,
  LibraryBig,
  Search,
  ScrollText,
  Sparkles,
} from "lucide-react";
import { books, loadKathaStory } from "../data/katha/catalog";
import KathaSeo, {
  breadcrumbSchema,
  kathaBookUrl,
  kathaStoryUrl,
  SITE_ORIGIN,
} from "../components/KathaSeo";
import KathaPagination from "../components/KathaPagination";
import "./KathaExperience.css";
import {
  AutoTranslated,
  KathaLanguageBar,
} from "../components/KathaAutoTranslation";

const BOOKS_PER_PAGE = 6;
const STORIES_PER_PAGE = 8;
const homeCrumb = { name: "होम", path: "/" };
const kathaCrumb = { name: "कथा संग्रह", path: "/katha/" };
const safePage = (value) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
};
const paged = (items, page, limit) =>
  items.slice((page - 1) * limit, page * limit);
const listingHref = (path, page) => (page <= 1 ? path : `${path}page/${page}/`);

function Breadcrumbs({ crumbs }) {
  return (
    <AutoTranslated>
      <nav aria-label="Breadcrumb" className="katha-breadcrumb">
        {crumbs.map((c, i) => (
          <React.Fragment key={c.path}>
            {i > 0 && (
              <span aria-hidden="true" className="katha-crumb-separator">
                /
              </span>
            )}
            {i === crumbs.length - 1 ? (
              <span aria-current="page">{c.name}</span>
            ) : (
              <Link to={c.path}>{c.name}</Link>
            )}
          </React.Fragment>
        ))}
      </nav>
    </AutoTranslated>
  );
}

export function BookCard({ book }) {
  return (
    <AutoTranslated>
      <article className="katha-book-card">
        <Link
          to={kathaBookUrl(book)}
          className="katha-book-link"
          aria-label={`${book.title} की सभी कथाएँ पढ़ें`}
        >
          <div className="katha-book-cover">
            <img
              src={book.image}
              alt={`${book.title} — पुस्तक चित्र`}
              loading="lazy"
              width="900"
              height="690"
            />
            <div className="katha-cover-info">
              <span className="katha-cover-top">
                {"VISHNU JI MAHARAJ · KATHA SERIES"}
              </span>
              <div className="katha-cover-text" style={{ textAlign: "center" , padding: "0 1rem",}}>
                <span className="katha-cover-ornament padding-2">✦ ✦ ✦</span>
                <strong className="katha-cover-title">{book.title}</strong>
                <p>{book.subtitle}</p>
              </div>
              <span className="katha-cover-bottom">
                {"हिंदी कथा"}
                <span aria-hidden="true">✧</span>
              </span>
            </div>
          </div>
          <div className="katha-book-body">
            <div className="katha-book-eyebrow">
              <span>{book.label}</span>
              <span>
                {book.stories.length} {"कथाएँ"}
              </span>
            </div>
            <h3>{book.title}</h3>
            <p>{book.description}</p>
            <span className="katha-inline-arrow">
              {"पुस्तक पढ़ें"}
              <ArrowRight size={17} aria-hidden="true" />
            </span>
          </div>
        </Link>
      </article>
    </AutoTranslated>
  );
}

function StoryCard({ story, book }) {
  return (
    <AutoTranslated>
      <article className="katha-story-card">
        <Link
          to={kathaStoryUrl(book, story)}
          className="katha-story-link"
          aria-label={`${story.title} पढ़ें`}
        >
          <div className="katha-story-thumb">
            <img
              src={story.image}
              alt={`${story.title} का कथा चित्र`}
              loading="lazy"
              width="900"
              height="690"
            />
            <span>
              {"अध्याय"} {String(story.chapter).padStart(2, "0")}
            </span>
          </div>
          <div className="katha-story-body">
            <p className="katha-chapter-small">
              {book.label} <span aria-hidden="true">·</span>
              {"हिंदी कथा"}
            </p>
            <h3>{story.title}</h3>
            <span className="katha-inline-arrow">
              {"पूरी कथा पढ़ें"}
              <ArrowRight size={17} aria-hidden="true" />
            </span>
          </div>
        </Link>
      </article>
    </AutoTranslated>
  );
}

// home/katha page ka hai
function KathaLibrary() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pageNum } = useParams();
  const query = (searchParams.get("q") || "").trim();
  const rawPage = pageNum || searchParams.get("page");
  const [searchValue, setSearchValue] = useState(query);
  useEffect(() => setSearchValue(query), [query]);
  const filtered = useMemo(
    () =>
      books.filter((b) =>
        `${b.title} ${b.subtitle} ${b.description} ${b.en}`
          .toLocaleLowerCase()
          .includes(query.toLocaleLowerCase()),
      ),
    [query],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / BOOKS_PER_PAGE));
  const currentPage = Math.min(safePage(rawPage), totalPages);
  const path = query ? "/katha/" : listingHref("/katha/", currentPage);
  const description =
    currentPage > 1 && !query
      ? "हनुमान चरित्र, भक्तों की प्रेरक कथाएँ और भगवद्गीता ज्ञान की हिंदी पुस्तकें पढ़ें। धार्मिक कहानियाँ और जीवन की सीख।"
      : `श्रीकृष्ण, रामायण, शिव महिमा, हनुमान और भक्ति की ${books.reduce((sum, book) => sum + book.stories.length, 0)} हिंदी कथाएँ पढ़ें। विषयवार किताबें, अध्याय और सरल भाषा में आध्यात्मिक कहानियाँ।`;
  const handleSearch = (event) => {
    event.preventDefault();
    const value = searchValue.trim();
    setSearchParams(value ? { q: value } : {});
  };
  return (
    <AutoTranslated>
      <main className="katha-page">
        <KathaSeo
          title={
            currentPage > 1 && !query
              ? `हिंदी कथा संग्रह – पृष्ठ ${currentPage} | Vishnu Ji Maharaj`
              : "हिंदी धार्मिक कथा संग्रह | Vishnu Ji Maharaj"
          }
          description={description}
          path={path}
          image="/katha-images/krishna.webp"
          noindex={!!query || (rawPage && safePage(rawPage) > totalPages)}
          schema={[
            breadcrumbSchema([homeCrumb, kathaCrumb]),
            {
              "@type": "CollectionPage",
              name: "हिंदी धार्मिक कथा संग्रह",
              url: `${SITE_ORIGIN}${path}`,
              inLanguage: "hi",
              description,
              mainEntity: {
                "@type": "ItemList",
                itemListElement: filtered.map((book, index) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  name: book.title,
                  url: `${SITE_ORIGIN}${kathaBookUrl(book)}`,
                })),
              },
            },
          ]}
        />
        <section className="katha-main-hero">
          <Container className="katha-hero-container">
            <Breadcrumbs crumbs={[homeCrumb, kathaCrumb]} />
            <Row className="align-items-center gy-5">
              <Col lg={7}>
                <span className="katha-eyebrow">
                  <span className="katha-sparkle">✦</span>
                  {"श्री विष्णु जी महाराज · आध्यात्मिक कथा संग्रह"}
                </span>
                <h1>
                  {"कथाओं में छिपा है"} <em>{"जीवन का ज्ञान।"}</em>
                </h1>
                <p className="katha-hero-description">
                  {
                    "रामायण की मर्यादा, कान्हा की लीलाएँ, महादेव की करुणा और भक्ति की अनमोल कहानियाँ — अब एक सुंदर डिजिटल पुस्तकालय में।"
                  }
                </p>
                <div className="katha-hero-actions">
                  <a href="#katha-books" className="katha-primary-btn">
                    {"कथाएँ पढ़ना शुरू करें"}
                    <ArrowRight size={18} />
                  </a>
                  <span className="katha-small-note">
                    {"निःशुल्क हिंदी कहानियाँ"}
                  </span>
                </div>
                <div className="katha-hero-stats">
                  <span>
                    <b>{books.length.toString().padStart(2, "0")}</b>
                    {"पुस्तकें"}
                  </span>
                  <span>
                    <b>{books.reduce((sum, b) => sum + b.stories.length, 0)}</b>
                    {"कथाएँ"}
                  </span>
                  <span>
                    <b>{"मूल भाषा"}</b>
                    {"हिंदी"}/ {"English"}
                  </span>
                </div>
              </Col>
              <Col lg={5}>
                <div className="katha-hero-art" aria-hidden="true">
                  <div className="katha-hero-orbit"></div>
                  <div className="katha-hero-book katha-hero-book-back">
                    <img src="/images/kathahome1.jpg" alt="krishnji ball lila" />
                  </div>
                  <div className="katha-hero-book katha-hero-book-front">
                   <img src="/images/kathahome2.jpg" alt="krishnji ball lila" />
                
                    <div>
                      {"श्रीकृष्ण"}
                      <small>{"बाल लीलाएँ"}</small>
                    </div>
                  </div>
                  <div className="katha-hero-seal">
                    {"ॐ"}
                    <span>{"शुभम्"}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
        <section className="katha-shelf-section" id="katha-books">
          <Container>
            <div className="katha-section-header">
              <div>
                <span className="katha-eyebrow">{"✦ हमारे कथा संग्रह"}</span>
                <h2>{"अपनी पसंद की पुस्तक चुनें"}</h2>
                <p>
                  {
                    "हर पुस्तक में सरल हिंदी में अलग-अलग धार्मिक प्रसंग और प्रेरक कहानियाँ।"
                  }
                </p>
              </div>
            </div>
            <form
              className="katha-search"
              role="search"
              onSubmit={handleSearch}
            >
              <label htmlFor="katha-search-input">
                <Search size={20} aria-hidden="true" />
                <span className="visually-hidden">{"पुस्तक खोजें"}</span>
              </label>
              <input
                id="katha-search-input"
                type="search"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={"पुस्तक खोजें, जैसे: कृष्ण, रामायण, शिव..."}
              />
              <button type="submit">{"खोजें"}</button>
            </form>
            <div className="katha-results-head">
              <span>{query ? `“${query}” के खोज परिणाम` : "सभी पुस्तकें"}</span>
              <span>
                {filtered.length} {"पुस्तकें"}
              </span>
            </div>
            {filtered.length ? (
              <Row className="g-4">
                {paged(filtered, currentPage, BOOKS_PER_PAGE).map((book) => (
                  <Col lg={4} md={6} key={book.slug}>
                    <BookCard book={book} />
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="katha-empty">
                <LibraryBig size={42} />
                <h3>{"कोई पुस्तक नहीं मिली"}</h3>
                <p>{"दूसरा शब्द लिखकर देखें।"}</p>
                <Link to="/katha/">{"सभी पुस्तकें देखें"}</Link>
              </div>
            )}
            {!query && (
              <KathaPagination
                current={currentPage}
                total={totalPages}
                createHref={(page) => listingHref("/katha/", page)}
                label="पुस्तक पृष्ठ"
              />
            )}
          </Container>
        </section>
        <section className="katha-inspiration">
          <Container>
            <span>✦</span>
            <p>{"“अच्छी कथा केवल सुनी नहीं जाती, जीवन में उतारी जाती है।”"}</p>
            <small>{"आइए, हर दिन एक नई सीख पढ़ें।"}</small>
          </Container>
        </section>
      </main>
    </AutoTranslated>
  );
}

// होम/कथा संग्रह/श्रीकृष्ण बाल लीलाएँ page ka hai
function KathaBook() {
  const { bookSlug, pageNum } = useParams();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");

  const book = books.find((b) => b.slug === bookSlug);

  const stories = book?.stories || [];

  const filteredStories = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase();

    if (!term) return stories;

    return stories.filter((story) =>
      `${story.title || ""} ${story.slug || ""} ${story.excerpt || ""} ${story.chapter ?? ""}`
        .toLocaleLowerCase()
        .includes(term),
    );
  }, [stories, searchTerm]);

  const rawPage = pageNum || searchParams.get("page");

  if (!book) return <KathaNotFound />;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredStories.length / STORIES_PER_PAGE)
  );

  const currentPage = Math.min(
    safePage(rawPage),
    totalPages
  );

  const bookPath = kathaBookUrl(book);
  const pagePath = listingHref(bookPath, currentPage);

  const crumbs = [
    homeCrumb,
    kathaCrumb,
    { name: book.title, path: bookPath },
  ];

  // Search active ho to filtered results dikhao.
  // Search empty ho to normal pagination use karo.
  const visibleStories = searchTerm.trim()
    ? filteredStories
    : paged(stories, currentPage, STORIES_PER_PAGE);

  return (
    <AutoTranslated>
      <main className="katha-page">
        <KathaSeo
          title={
            currentPage > 1
              ? `${book.title} – अध्याय पृष्ठ ${currentPage} | Vishnu Ji Maharaj`
              : book.metaTitle
          }
          description={
            currentPage > 1
              ? `${book.title} के अगले अध्याय, हिंदी कहानियाँ और प्रेरक प्रसंग पढ़ें। ${book.subtitle} का सरल संग्रह।`
              : book.metaDescription
          }
          path={pagePath}
          image={book.image}
          noindex={!!rawPage && safePage(rawPage) > totalPages}
          schema={[
            breadcrumbSchema(crumbs),
            {
              "@type": "Book",
              name: book.title,
              inLanguage: "hi",
              description: book.description,
              url: `${SITE_ORIGIN}${bookPath}`,
              image: `${SITE_ORIGIN}${book.image}`,
              publisher: {
                "@type": "Organization",
                name: "Vishnu Ji Maharaj",
              },
              hasPart: book.stories.map((story) => ({
                "@type": "CreativeWork",
                name: story.title,
                url: `${SITE_ORIGIN}${kathaStoryUrl(book, story)}`,
              })),
            },
          ]}
        />

        <section className="katha-book-hero">
          <Container>
            <Breadcrumbs crumbs={crumbs} />

            <Row className="align-items-center gy-4">
              <Col md={5} lg={4}>
                <div className="katha-featured-cover">
                  <img
                    src={book.image}
                    alt={`${book.title} की कवर इमेज`}
                  />

                  <div className="katha-featured-text">
                    <span>पुस्तक संग्रह</span>
                    <strong>{book.title}</strong>
                    <small>{book.subtitle}</small>
                  </div>
                </div>
              </Col>

              <Col md={7} lg={8} className="katha-book-hero-content">
                <span className="katha-eyebrow">
                  ✦ {book.label}
                </span>

                <h1>{book.title}</h1>
                <h2>{book.subtitle}</h2>
                <p>{book.description}</p>

                <div className="katha-detail-facts">
                  <span>
                    <BookOpen size={19} /> {book.stories.length} अध्याय
                  </span>
                  <span>
                    <Heart size={19} /> {book.reading}
                  </span>
                  <span>
                    <ScrollText size={19} /> हिंदी
                  </span>
                </div>

                <a href="#chapters" className="katha-primary-btn">
                  सभी अध्याय देखें
                  <ArrowRight size={18} />
                </a>
              </Col>
            </Row>
          </Container>
        </section>

        <section
          className="katha-chapters-section"
          id="chapters"
        >
          <Container>
            <Form.Control
              type="search"
              placeholder="कथा खोजें..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="katha-search-form"
              aria-label="कथा खोजें"
            />

            <div className="katha-section-header">
              <div>
                <span className="katha-eyebrow">
                  ✦ पुस्तक के अध्याय
                </span>

                <h2>पढ़ने के लिए एक कथा चुनें</h2>

                <p>
                  कहानी पर क्लिक करें और पूरा प्रसंग सरल हिंदी में पढ़ें।
                </p>
              </div>

              <Link to="/katha/" className="katha-secondary-link">
                <ArrowLeft size={17} />
                सभी पुस्तकें
              </Link>
            </div>

            <Row className="g-4">
              {visibleStories.length > 0 ? (
                visibleStories.map((story) => (
                  <Col lg={6} key={story.slug}>
                    <StoryCard book={book} story={story} />
                  </Col>
                ))
              ) : (
                <Col xs={12}>
                  <div className="katha-no-results">
                    <h3>कोई कथा नहीं मिली</h3>
                    <p>
                      दूसरा शब्द खोजें या सभी कथाएँ देखने के लिए
                      खोज साफ़ करें।
                    </p>

                    <button
                      type="button"
                      className="katha-secondary-link"
                      onClick={() => setSearchTerm("")}
                    >
                      सभी कथाएँ देखें
                    </button>
                  </div>
                </Col>
              )}
            </Row>

            {!searchTerm.trim() && (
              <KathaPagination
                current={currentPage}
                total={Math.ceil(
                  stories.length / STORIES_PER_PAGE
                )}
                createHref={(page) => listingHref(bookPath, page)}
                label="अध्याय पृष्ठ"
              />
            )}
          </Container>
        </section>
      </main>
    </AutoTranslated>
  );
}


const READER_COPY = {
  chapter: "अध्याय",
  type: "हिंदी धार्मिक कथा",
  collection: "विष्णु जी महाराज कथा संग्रह",
  full: "कथा का सार",
  shareTitle: "इस कथा को दूसरों के साथ साझा करें",
  shareHint: "भक्ति का संदेश आगे बढ़ाएँ।",
  copy: "कथा का लिंक कॉपी करें",
  copied: "लिंक कॉपी हो गया ✓",
  previous: "पिछली कथा",
  next: "अगली कथा",
  allChapters: "के सभी अध्याय देखें",
  reading: "आप पढ़ रहे हैं",
  allBook: "पूरी पुस्तक",
  related: "इसी पुस्तक की अन्य कथाएँ",
  imageAlt: "चित्रात्मक प्रस्तुति",
  loading: "कथा खुल रही है…",
};

function KathaStory() {
  const { bookSlug, storySlug } = useParams();
  const book = books.find((b) => b.slug === bookSlug);
  const summary = book?.stories.find((s) => s.slug === storySlug);
  const [story, setStory] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const labels = READER_COPY;
  const path = book && summary ? kathaStoryUrl(book, summary) : "/katha/";

  useEffect(() => {
    let active = true;
    setStory(null);
    setLoadFailed(false);
    setCopied(false);
    if (summary) {
      // Each story is loaded independently from its original Hindi JSON.
      loadKathaStory(bookSlug, storySlug)
        .then((data) => {
          if (active) setStory({ ...data, chapter: summary.chapter });
        })
        .catch(() => {
          if (active) setLoadFailed(true);
        });
    }
    return () => {
      active = false;
    };
  }, [bookSlug, storySlug, summary]);

  if (!book || !summary || loadFailed) return <KathaNotFound />;
  if (!story) {
    return (
      <AutoTranslated>
        <main className="katha-page katha-reading-page">
          <KathaSeo
            title={summary.metaTitle}
            description={summary.metaDescription}
            path={path}
            image={summary.image}
            type="article"
          />
          <Container
            className="katha-reading-container"
            role="status"
            aria-live="polite"
          >
            <p className="katha-loading-message">{labels.loading}</p>
          </Container>
        </main>
      </AutoTranslated>
    );
  }
  const currentIndex = book.stories.findIndex((s) => s.slug === storySlug);
  const prev = book.stories[currentIndex - 1];
  const next = book.stories[currentIndex + 1];
  const crumbs = [
    { name: "होम", path: "/" },
    { name: "कथा संग्रह", path: "/katha/" },
    { name: book.title, path: kathaBookUrl(book) },
    { name: story.title, path },
  ];
  const share = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE_ORIGIN}${path}`);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <AutoTranslated>
      <main className="katha-page katha-reading-page">
        <KathaSeo
          title={story.metaTitle}
          description={story.metaDescription}
          path={path}
          image={story.image}
          type="article"
          schema={[
            breadcrumbSchema(crumbs),
            {
              "@type": "Article",
              headline: story.title,
              description: story.metaDescription,
              inLanguage: "hi",
              articleSection: book.title,
              mainEntityOfPage: `${SITE_ORIGIN}${path}`,
              image: `${SITE_ORIGIN}${story.image}`,
              isPartOf: {
                "@type": "Book",
                name: book.title,
                url: `${SITE_ORIGIN}${kathaBookUrl(book)}`,
              },
              publisher: {
                "@type": "Organization",
                name: "Vishnu Ji Maharaj",
                logo: {
                  "@type": "ImageObject",
                  url: `${SITE_ORIGIN}/logo.png`,
                },
              },
            },
          ]}
        />
        <Container className="katha-reading-container">
          <Breadcrumbs crumbs={crumbs} />
          <Row className="g-5">
            <Col lg={8}>
              <article className="katha-article">
                <div className="katha-article-category">
                  <Bookmark size={16} /> {book.title} <span> / </span>{" "}
                  {labels.chapter} {story.chapter}
                </div>
                <h1>{story.title}</h1>
                <p className="katha-article-deck">{story.excerpt}</p>
                <div className="katha-article-meta">
                  <span>
                    <BookOpen size={17} /> {labels.type}
                  </span>
                  <span>✦</span>
                  <span>{labels.collection}</span>
                </div>
                <figure className="katha-article-figure">
                  <img
                    src={story.image}
                    alt={`${story.title} — ${labels.imageAlt}`}
                    width="900"
                    height="690"
                  />
                  <figcaption>
                    {story.title} — {book.title}
                  </figcaption>
                </figure>
                <div className="katha-prose">
                  <p className="katha-first-paragraph">{story.lead}</p>
                  {story.sections.map((section, i) => (
                    <section
                      key={`${section.heading}-${i}`}
                      id={`katha-part-${i + 1}`}
                      className="katha-prose-section"
                    >
                      <h2>{section.heading}</h2>
                      {section.paragraphs.map((paragraph, pIndex) => (
                        <p key={pIndex}>{paragraph}</p>
                      ))}
                    </section>
                  ))}
                  <div className="katha-lesson">
                    <span>✦ {labels.full}</span>
                    <p>{story.conclusion}</p>
                  </div>
                </div>
                <div className="katha-article-cta">
                  <div>
                    <span>{labels.shareTitle}</span>
                    <p>{labels.shareHint}</p>
                  </div>
                  <button type="button" onClick={share}>
                    {copied ? labels.copied : labels.copy}
                  </button>
                </div>
                <nav
                  className="katha-story-neighbors"
                  aria-label="Other chapters"
                >
                  {prev ? (
                    <Link to={kathaStoryUrl(book, prev)}>
                      <span>
                        <ArrowLeft size={16} /> {labels.previous}
                      </span>
                      <strong>{prev.title}</strong>
                    </Link>
                  ) : (
                    <span className="katha-neighbor-blank" />
                  )}
                  {next ? (
                    <Link to={kathaStoryUrl(book, next)}>
                      <span>
                        {labels.next} <ArrowRight size={16} />
                      </span>
                      <strong>{next.title}</strong>
                    </Link>
                  ) : (
                    <span className="katha-neighbor-blank" />
                  )}
                </nav>
                <Link className="katha-back-book" to={kathaBookUrl(book)}>
                  <ArrowLeft size={17} /> {book.title} {labels.allChapters}
                </Link>
              </article>
            </Col>
            <Col lg={4}>
              <aside className="katha-reading-sidebar">
                <div className="katha-sidebar-book">
                  <img src={book.image} alt="" width="900" height="690" />
                  <div className="katha-sidebar-book-info padding-5">
                    <span>{labels.reading}</span>
                    <h2>{book.title}</h2>
                    <p>{book.subtitle}</p>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Link
                        to={kathaBookUrl(book)}
                        className="katha-primary-btn"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {labels.allBook}
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="katha-sidebar-related">
                  <h2>{labels.related}</h2>
                  {book.stories
                    .filter((s) => s.slug !== story.slug)
                    .slice(0, 4)
                    .map((s) => (
                      <Link key={s.slug} to={kathaStoryUrl(book, s)}>
                        <span>{String(s.chapter).padStart(2, "0")}</span>
                        <strong>{s.title}</strong>
                        <ArrowRight size={15} />
                      </Link>
                    ))}
                </div>
              </aside>
            </Col>
          </Row>
        </Container>
      </main>
    </AutoTranslated>
  );
}

function KathaNotFound() {
  return (
    <AutoTranslated>
      <main className="katha-page katha-not-found">
        <KathaSeo
          title="कथा नहीं मिली | Vishnu Ji Maharaj"
          description="कथा संग्रह में उपलब्ध हिंदी कहानियाँ देखें।"
          path="/katha/"
          noindex={true}
        />
        <Container>
          <Sparkles size={45} />
          <h1>{"यह कथा उपलब्ध नहीं है"}</h1>
          <p>{"कृपया किसी दूसरी पुस्तक या कहानी का चयन करें।"}</p>
          <Link to="/katha/" className="katha-primary-btn">
            {"कथा संग्रह पर जाएँ"}
            <ArrowRight size={18} />
          </Link>
        </Container>
      </main>
    </AutoTranslated>
  );
}

export default function KathaExperience() {
  const { bookSlug, storySlug } = useParams();
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname, location.search]);
  const content = storySlug ? (
    <KathaStory key={bookSlug + storySlug} />
  ) : bookSlug ? (
    <KathaBook key={bookSlug} />
  ) : (
    <KathaLibrary />
  );
  return (
    <>
      <KathaLanguageBar />
      {content}
    </>
  );
}
